import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  FolderKanban, UserCheck, CheckSquare, Sparkles, TrendingUp, Wallet, AlertTriangle, Activity,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, accent, testid, sub }) {
  return (
    <div
      data-testid={testid}
      className="relative bg-white rounded-lg border border-[#D7C6A9] p-4 hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)] transition-shadow overflow-hidden"
    >
      {accent && <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#F56B1F]" />}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-neutral-500 uppercase tracking-wider">{label}</div>
          <div className="mono text-3xl font-extrabold tracking-tight text-neutral-900 mt-1.5">{value}</div>
          {sub && <div className="text-xs text-neutral-400 mt-1">{sub}</div>}
        </div>
        <div className="h-9 w-9 rounded-md bg-[#EEE9DE] flex items-center justify-center">
          <Icon size={18} className="text-[#F56B1F]" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/management").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <AppShell title="Dashboard"><div className="text-sm text-neutral-400">Loading…</div></AppShell>;

  const statusEntries = Object.entries(d.projects_by_status || {});
  const maxCount = Math.max(1, ...statusEntries.map(([, v]) => v));

  return (
    <AppShell title={`Good day, ${user?.first_name || ""}`}>
      <p className="text-sm text-neutral-500 -mt-3 mb-6">Management overview · real-time operating snapshot</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Active Projects" value={d.business.active_projects} icon={FolderKanban} accent testid="kpi-card-active-projects-value" />
        <Kpi label="Customers" value={d.business.customers} icon={UserCheck} testid="kpi-card-customers-value" />
        <Kpi label="Pending Approvals" value={d.operations.pending_approvals} icon={CheckSquare} testid="kpi-card-pending-approvals-value" />
        <Kpi label="AI Recommendations" value={d.ai.critical_recommendations} icon={Sparkles} testid="kpi-card-ai-recos-value" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Pipeline */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-[#D7C6A9] p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-semibold text-neutral-900">Project Pipeline</h2>
            <Activity size={16} className="text-neutral-400" />
          </div>
          {statusEntries.length === 0 ? (
            <div className="text-sm text-neutral-400 py-8 text-center">No projects yet.</div>
          ) : (
            <div className="space-y-3">
              {statusEntries.map(([status, count]) => (
                <div key={status} className="flex items-center gap-3" data-testid={`pipeline-row-${status}`}>
                  <div className="w-40 shrink-0"><StatusBadge status={status} /></div>
                  <div className="flex-1 h-2.5 rounded-full bg-[#EEE9DE] overflow-hidden">
                    <div className="h-full bg-[#F56B1F] rounded-full transition-all" style={{ width: `${(count / maxCount) * 100}%` }} />
                  </div>
                  <div className="mono text-sm text-neutral-700 w-8 text-right">{count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Finance snapshot (zero state, no fabricated data) */}
        <div className="bg-white rounded-lg border border-[#D7C6A9] p-5">
          <h2 className="font-heading text-base font-semibold text-neutral-900 mb-4">Finance Snapshot</h2>
          <div className="space-y-4">
            {[
              { label: "Revenue", value: inr(d.business.revenue), icon: TrendingUp },
              { label: "Receivables", value: inr(d.business.receivables), icon: Wallet },
              { label: "Gross Margin", value: `${d.finance.gross_margin}%`, icon: TrendingUp },
              { label: "Margin Risk", value: d.finance.margin_risk, icon: AlertTriangle },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-neutral-600"><row.icon size={15} className="text-neutral-400" />{row.label}</span>
                <span className="mono text-sm font-medium text-neutral-900">{row.value}</span>
              </div>
            ))}
            <p className="text-[11px] text-neutral-400 pt-2 border-t border-[#EEE9DE]">Finance module ships in a later build — figures populate from real data only.</p>
          </div>
        </div>
      </div>

      {/* Recent projects */}
      <div className="bg-white rounded-lg border border-[#D7C6A9] mt-6 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#EEE9DE] flex items-center justify-between">
          <h2 className="font-heading text-base font-semibold text-neutral-900">Recent Projects</h2>
          <button onClick={() => navigate("/projects")} className="text-sm text-[#F56B1F] hover:underline" data-testid="dashboard-view-all-projects">View all</button>
        </div>
        {d.recent_projects.length === 0 ? (
          <div className="text-sm text-neutral-400 py-10 text-center">No projects yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]">
                <th className="px-5 py-2.5 font-medium">Project ID</th>
                <th className="px-5 py-2.5 font-medium">Name</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.recent_projects.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="border-t border-[#EEE9DE] hover:bg-[#EEE9DE] cursor-pointer transition-colors" data-testid={`dashboard-project-row-${p.project_code}`}>
                  <td className="px-5 py-3 mono text-neutral-700">{p.project_code}</td>
                  <td className="px-5 py-3 text-neutral-900">{p.project_name}</td>
                  <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}
